<template>
  <div v-if="!isAuthenticating">
    <!-- Show login screen if not authenticated -->
    <div v-if="!isAuthenticated" class="auth-container">
      <div class="auth-content">
        <h1>CBRE TOPS2 Email Agent</h1>
        <p v-if="authError" class="auth-error">{{ authError }}</p>
        <v-btn color="#003F2D" @click="handleLogin" :loading="isAuthenticating" block>
          Sign in to continue
        </v-btn>
      </div>
    </div>

    <!-- Show main app content if authenticated -->
    <div v-else class="app-container">
      <div style="background: #fff;">
        <Header />
        <div class="content-section">
          <div v-if="selectedTab === 'Actions'" class="section-card">
            <SelectedEmailAndAssignment 
              :emailData="currentEmail" 
              :initiallyCollapsed="false"
              :showCollapseIcon="false"
            />
            <Notification />
            <AssignmentsBar />
            <QuickActions ref="quickActions" />
            <!-- <AdditionalActions /> -->
            <RecentActions />
            
            <!-- Version display at bottom -->
            <div class="version-footer">
              <div class="version-info">
                <span class="version-label" :title="versionTooltip">{{ versionLabel }}</span>
                <span class="build-time" v-if="versionInfo && versionInfo.buildTime">{{ formatBuildTime }}</span>
              </div>
            </div>
          </div>
          <div v-else-if="selectedTab === 'Context'" class="section-card">
            <SelectedEmailAndAssignment 
              :emailData="currentEmail" 
              :assignment="selectedAssignment" 
              :initiallyCollapsed="false"
            />
            <ContextCapture />
            
            <!-- Version display at bottom -->
            <div class="version-footer">
              <div class="version-info">
                <span class="version-label" :title="versionTooltip">{{ versionLabel }}</span>
                <span class="build-time" v-if="versionInfo && versionInfo.buildTime">{{ formatBuildTime }}</span>
              </div>
            </div>
          </div>
          <div v-else-if="selectedTab === 'Notifications'" class="section-card">
            <SelectedEmailAndAssignment 
              :emailData="currentEmail" 
              :assignment="selectedAssignment" 
              :initiallyCollapsed="false"
            />
            <Notifications />
            
            <!-- Version display at bottom -->
            <div class="version-footer">
              <div class="version-info">
                <span class="version-label" :title="versionTooltip">{{ versionLabel }}</span>
                <span class="build-time" v-if="versionInfo && versionInfo.buildTime">{{ formatBuildTime }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="auth-container">
    <div class="auth-content">
      <v-progress-circular indeterminate color="#003F2D"></v-progress-circular>
      <p>Checking authentication status...</p>
    </div>
  </div>
</template>

<script>
import Header from "./commons/Header.vue";
import TabsBar from "./commons/TabsBar.vue";
import SelectedEmailAndAssignment from "./commons/SelectedEmailAndAssignment.vue";
import AdditionalActions from "./actions/AdditionalActions.vue";
import RecentActions from "./actions/RecentActions.vue";
import ContextCapture from "./context/ContextCapture.vue";
import Notifications from "./notifications/Notifications.vue";
import Notification from "./commons/Notification.vue";
import { mapState, mapActions, mapGetters } from "vuex";
import AssignmentsBar from "./actions/assignment/AssignmentsBar.vue";
import QuickActions from "./actions/QuickActions.vue";
import tokenService from '../service/TokenService';
import { fetchUserDetails } from "../service/engagement.js";
import DatadogService from '../service/DatadogService.js';
import CacheService from '../service/CacheService.js';
import VersionService from '../service/VersionService.js';
import moment from 'moment';

export default {
  name: "TaskPane",
  components: {
    Header,
    TabsBar,
    SelectedEmailAndAssignment,
    AssignmentsBar,
    QuickActions,
    AdditionalActions,
    RecentActions,
    ContextCapture,
    Notifications,
    Notification,
  },
  data() {
    return {
      accessToken: "",
      selectedTab: "Actions",
      officeInitialized: false,
      emailData: {},
      token: '',
      error: '',
      showDueDateDialog: false,
      isDataLoaded: true, // Start with true so interface shows by default
      isAuthenticating: true,
      isAuthenticated: false,
      authError: null,
      versionLabel: 'Loading...',
      versionInfo: null,
    };
  },
  computed: {
    ...mapState({
      currentEmail: (state) => state.email.currentEmail,
      officeReady: (state) => state.office?.officeReady,
      isAuthenticated: (state) => state.auth.isAuthenticated,
      isAuthenticating: (state) => state.auth.isAuthenticating,
      authError: (state) => state.auth.authError,
    }),
    ...mapGetters({
      getLoggedInUserEmailAddress: "email/getLoggedInUserEmailAddress",
      selectedAssignment: "assignments/selectedAssignment",
      activeAction: "ui/activeAction",
      isEligibleForPropertyFollowUp: "assignments/isEligibleForPropertyFollowUp",
      propertyFollowUpIneligibilityReason: "assignments/propertyFollowUpIneligibilityReason",
    }),
    selectedTab() {
      return this.$store.state.selectedTab;
    },
    versionTooltip() {
      if (!this.versionInfo) return 'Loading version info...'
      
      const lines = []
      lines.push(`Add-in: TOPS2 Actions`)
      if (this.versionInfo.manifestVersion && this.versionInfo.manifestVersion !== this.versionInfo.packageVersion) {
        lines.push(`Deployed Version: ${this.versionInfo.manifestVersion}`)
      }
      lines.push(`Environment: ${this.versionInfo.environment}`)
      if (this.versionInfo.buildTime) {
        const buildDate = new Date(this.versionInfo.buildTime).toLocaleString()
        lines.push(`Build Time: ${buildDate}`)
      }
      
      return lines.join('\n')
    },
    formatBuildTime() {
      if (!this.versionInfo || !this.versionInfo.buildTime) return ''
      const date = new Date(this.versionInfo.buildTime)
      return `Built ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    }
  },
  watch: {
    isAuthenticated(newValue) {
      if (newValue) {
        this.initializeAppData();
      }
    },
    // Re-evaluate warning when selected assignment changes
    // Also pre-fetch eligible IDs so the banner can show on the main page
    // without waiting for the user to open a dialog
    selectedAssignment: {
      async handler(newVal) {
        if (!newVal) return;
        // Eagerly load both ID lists so tile disabling works on the main page
        // before the user opens a dialog. Both are lightweight single API calls.
        const dateStart = moment().subtract(20, 'days').toDate();
        const dateEnd   = moment().toDate();
        await Promise.all([
          this.loadPropertyFollowUpEligibleIds(),
          this.loadClientFollowUpEligibleIds({ dateStart, dateEnd }),
        ]);
      },
      deep: true,
      immediate: true,
    },
    // Fetch backend IDs when a dialog opens so tile state stays current
    activeAction: {
      async handler(newAction) {
        if (!newAction || !this.selectedAssignment) return;
        if (newAction === 'propertyFollowUp') {
          await this.loadPropertyFollowUpEligibleIds();
        } else if (newAction === 'clientFollowUp') {
          const dateStart = moment().subtract(20, 'days').toDate();
          const dateEnd = moment().toDate();
          await this.loadClientFollowUpEligibleIds({ dateStart, dateEnd });
        }
      },
    },
    // Watch for email changes when task pane is pinned
    currentEmail: {
      handler(newEmail, oldEmail) {
        console.log('🔍 Email watcher triggered:');
        console.log('New email:', newEmail);
        console.log('Old email:', oldEmail);
        
        if (newEmail && newEmail.internetMessageId && 
            oldEmail && oldEmail.internetMessageId && 
            newEmail.internetMessageId !== oldEmail.internetMessageId) {
          console.log('📧 Email changed via pinning, refreshing data');
          console.log('New Message ID:', newEmail.internetMessageId);
          console.log('Old Message ID:', oldEmail.internetMessageId);
          
          // Clear notifications from previous email
          this.$store.commit('notification/CLEAR_NOTIFICATIONS');
          
          // Refresh folder context in QuickActions component
          this.$nextTick(() => {
            if (this.$refs.quickActions && this.$refs.quickActions.refreshFolderContext) {
              this.$refs.quickActions.refreshFolderContext();
              console.log('🔄 Triggered folder context refresh in QuickActions');
            }
          });
          
        } else {
          console.log('📧 Email watcher fired but no message ID change detected');
        }
      },
      deep: true,
      immediate: false
    },
    // Also watch the loading state
    'currentEmail.subject': {
      handler(newSubject, oldSubject) {
        console.log('📧 Subject changed from:', oldSubject, 'to:', newSubject);
      }
    }
  },
  async created() {
    try {
      console.log('TaskPane created, checking cache version...');
      
      // Check if cache needs to be cleared due to version update
      const cacheCleared = await CacheService.checkCacheVersion();
      if (cacheCleared) {
        console.log('Cache was cleared due to version update');
      }
      
      console.log('Attempting silent sign-in...');
      await this.attemptSilentSignIn();
    } catch (error) {
      console.warn('Silent sign-in failed:', error);
      this.isAuthenticating = false;
    }
    this.getBox();
    this.checkAuthStatus();
    
    // Load version info
    await this.loadVersionInfo();
    
    // Expose admin utilities globally (development only)
    if (this.versionInfo && this.versionInfo.environment === 'development' && window) {
      window.TOPS2Admin = {
        refreshVersion: () => this.refreshVersion(),
        getVersionInfo: () => this.versionInfo,
        getVersionService: () => VersionService
      };
      console.log('Admin utilities available: window.TOPS2Admin');
    }
  },
  methods: {
    ...mapActions({ 
      loadCurrentEmail: "email/loadCurrentEmail", 
      getAccessToken: "email/getAccessToken", 
      add: "notification/add",
      fetchLookups: "lookup/fetchLookups",
      checkAuthStatus: "auth/checkAuthStatus",
      login: "auth/login",
      setLoggedInUserDetails: "email/setLoggedInUserDetails",
      loadPropertyFollowUpEligibleIds: "assignments/loadPropertyFollowUpEligibleIds",
      loadClientFollowUpEligibleIds: "assignments/loadClientFollowUpEligibleIds",
    }),

    // ──────────────────────────────────────────────────────────────────────

    async attemptSilentSignIn() {
      try {
        await tokenService.initialize();
        const token = await tokenService.silentSignIn();
        if (token) {
          console.log('Silent sign-in successful');
          this.isAuthenticated = true;
          await this.initializeAppData();
        }
      } catch (error) {
        console.warn('Silent sign-in failed:', error);
      } finally {
        this.isAuthenticating = false;
      }
    },
    async handleLogin() {
      this.isAuthenticating = true;
      this.authError = null;
      try {
        const token = await tokenService.getTops2Token();
        if (token) {
          this.isAuthenticated = true;
          
          // Track successful login in Datadog
          DatadogService.addAction('user_login', {
            authentication_method: 'tops2_token',
            success: true
          });
          
          await this.initializeAppData();
        }
      } catch (error) {
        console.error('Login failed:', error);
        this.authError = 'Failed to sign in. Please try again.';
        
        // Track login failure in Datadog
        DatadogService.addError(error, {
          error_type: 'authentication_error',
          authentication_method: 'tops2_token'
        });
        
      } finally {
        this.isAuthenticating = false;
      }
    },
    async initializeAppData() {
      try {
        // These operations can be done in parallel
        await Promise.all([
          this.loadCurrentEmail(),
          this.getAccessToken(),
          this.fetchLookups()
        ]);

        // Always fetch user details after loading data, even if no email is selected
        this.fetchUserDetails();

      } catch (error) {
        console.error('Failed to load initial data:', error);
        this.authError = 'Failed to load data. Please refresh the page.';
      }
    },
    getBox() {
      var my_awesome_script = document.createElement('script')
      var my_decent_script = document.createElement('script')

      my_awesome_script.setAttribute(
        'src',
        'https://cdn01.boxcdn.net/platform/elements/15.0.0/en-US/picker.js'
      )
      my_decent_script.setAttribute(
        'src',
        'https://cdn01.boxcdn.net/platform/elements/15.0.0/en-US/uploader.js'
      )

      var my_awesome_link = document.createElement('link')
      my_awesome_link.setAttribute('rel', 'stylesheet')
      my_awesome_link.setAttribute(
        'href',
        'https://cdn01.boxcdn.net/platform/elements/15.0.0/en-US/picker.css'
      )

      document.head.appendChild(my_awesome_script)
      document.head.appendChild(my_decent_script)
      document.head.appendChild(my_awesome_link)
    },
    fetchUserDetails() {
      // Always set isDataLoaded to true so the interface shows
      this.isDataLoaded = true;
      
      // Try to get user email from selected email first, then from Office profile
      let userEmail = this.getLoggedInUserEmailAddress;
      
      if (!userEmail && typeof Office !== "undefined" && Office.context?.mailbox?.userProfile?.emailAddress) {
        userEmail = Office.context.mailbox.userProfile.emailAddress;
        console.log('Using Office user profile email:', userEmail);
      }
      
      if (userEmail) {
        fetchUserDetails(userEmail)
          .then(userDetails => {
            console.log('User Details:', userDetails);
            this.setLoggedInUserDetails(userDetails);
            
            // Set user in Datadog for tracking
            DatadogService.setUser(userEmail, {
              user_id: userDetails?.id,
              name: userDetails?.name,
              department: userDetails?.department
            });
            
            // Track successful user details fetch
            DatadogService.addAction('user_details_loaded', {
              user_email: userEmail,
              has_user_details: !!userDetails,
              source: this.getLoggedInUserEmailAddress ? 'selected_email' : 'office_profile'
            });
            
          })
          .catch(error => {
            console.error('Error fetching user details:', error);
            
            // Track user details fetch error
            DatadogService.addError(error, {
              error_type: 'user_details_fetch_error',
              user_email: userEmail
            });
            
            this.add({
              type: 'error',
              message: 'Failed to fetch user details.',
              timeout: 5000
            });
          });
      } else {
        console.warn('No user email address found (neither from selected email nor Office profile).');
        
        // Track missing user email
        DatadogService.addAction('user_email_missing', {
          context: 'fetch_user_details',
          has_selected_email: !!this.getLoggedInUserEmailAddress,
          has_office_profile: !!(typeof Office !== "undefined" && Office.context?.mailbox?.userProfile?.emailAddress)
        });
      }
    },
    async loadVersionInfo() {
      try {
        this.versionLabel = await VersionService.getVersionLabel()
        this.versionInfo = await VersionService.getVersionInfo()
        console.log('Version info loaded:', this.versionInfo)
      } catch (error) {
        console.warn('Could not load version info:', error)
        this.versionLabel = `v1.0.0.5`
        this.versionInfo = { 
          packageVersion: '1.0.0.5',
          manifestVersion: null,
          environment: 'development',
          buildTime: null
        }
      }
    },
    async refreshVersion() {
      try {
        await VersionService.refreshVersion()
        await this.loadVersionInfo()
        
        // Show notification
        this.add({
          type: 'success',
          message: `Version refreshed: ${this.versionLabel}`,
          timeout: 5
        });
      } catch (error) {
        console.error('Failed to refresh version:', error)
        this.add({
          type: 'error',
          message: 'Failed to refresh version information',
          timeout: 5
        });
      }
    }
  },
  beforeUnmount() {
    // Clean up email change detection
    console.log('TaskPane unmounting, cleaning up email detection');
    if (window.stopEmailChangeDetection) {
      window.stopEmailChangeDetection();
    }
    
    // Clean up Office event listeners
    if (typeof Office !== "undefined" && Office.context?.mailbox?.removeHandlerAsync) {
      Office.context.mailbox.removeHandlerAsync(
        Office.EventType.ItemChanged,
        function(result) {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log('Office ItemChanged event handler removed successfully');
          } else {
            console.warn('Failed to remove Office ItemChanged event handler:', result.error);
          }
        }
      );
    }
  }
};
</script>

<style scoped>
.app-container {
  width: 100%;
  min-width: 0;
  height: 100%;
  margin: 0 auto;
  background: #fff;
  box-sizing: border-box;
  padding-bottom: 16px;
  padding-right: 19px; /* Restore right padding */
  overflow-x: hidden;
  overflow-y: auto;
}

/* Ensure main containers maintain minimum width and proper overflow */
.auth-container,
.taskpane-wrapper,
.content-section,
.section-card {
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* Main app container - fit host, let Outlook handle overflow */
.taskpane-wrapper {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: visible; /* Remove auto, let host control */
  box-sizing: border-box;
  position: relative;
}

/* 2. Auth container */
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
}
.auth-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: auto;
}

/* 3. Content section - Core fix for container width */
.content-section {
  padding: 16px 19px 24px 0; /* Restore right padding */
  min-width: 0;
  overflow-x: hidden; /* Restore hidden, not auto */
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  height: auto;
}

/* 4. Section cards - Simple styling */
.section-card {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  word-break: break-word;
  padding: 19px 8px; /* Reduced padding */
  border: 1px solid #e0e0e0;
  margin: 8px 0;
  height: auto;
}

/* 5. QuickActions wrapper - Minimal constraints */
.quick-actions-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: visible;
  box-sizing: border-box;
  margin: 8px 0;
  padding: 0; /* No padding to maximize space */
}

.auth-content {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.auth-content h1 {
  color: #003F2D;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.auth-error {
  color: #dc3545;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

/* Existing styles */
.content-section {
  padding: 16px 19px;
}

.section-card {
  background: #FFFFFF;
  border-radius: 8px;
}

/* Version footer styling */
.version-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
}

.version-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  min-width: 0;
  flex-wrap: wrap;
}

.version-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  cursor: help;
  user-select: none;
  background: #f9fafb;
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.version-label:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.build-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.build-time {
  font-size: 10px;
  color: #9ca3af;
  font-style: italic;
  white-space: nowrap;
}

/* Comprehensive Responsive Design System */

/* Extra Small (Mobile Portrait - 320px and below) */
@media (max-width: 320px) {
  .app-container {
  padding-right: 35px;
  /* 0 !important; */
  width: 100%;
  height: 100%; /* Remove height: 100vh */
  margin: 0 auto;
  background: #fff;
  box-sizing: border-box;
}
.auth-container {
    padding: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    min-height: 100vh !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    position: fixed !important;
    top: 0;
    left: 0;
    z-index: 9999;
    box-sizing: border-box;
  }
  .auth-content {
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: center !important;
    padding: 1rem !important;
    width: 80vw !important;
    max-width: 90vw !important;
    margin: 0 auto !important;
  }
  .version-footer {
    margin-top: 8px;
    padding-top: 8px;
    padding-right: 50px !important;
  }
  
  .version-info {
    gap: 4px;
    flex-wrap: wrap;
    flex-direction: column;
    text-align: center;
  }
  
  .version-label {
    font-size: 8px;
    padding: 1px 6px;
  }
  
  .build-time {
    font-size: 7px;
  }
  
  .content-section {
    padding: 2px;
  }
  
  .auth-container {
    padding: 8px;
    width: 100%;
    height: 100%; /* Remove min-height: 100vh */
  }
  
  .auth-content {
    padding: 12px;
    max-width: 100%;
    border-radius: 6px;
    margin-right: 64px;
  }
  
  .auth-content h1 {
    font-size: 14px;
    margin-bottom: 8px;
    line-height: 1.2;
    text-align: center;
  }
  
  .section-card {
    padding-right: 32px;
    margin-bottom: 4px;
    border-radius: 4px;
  }
}

/* Small (Mobile Landscape - 321px to 480px) */
@media (min-width: 321px) and (max-width: 480px) {
  .auth-container {
    padding: 12px;
    width: 100%;
    height: 100%; /* Remove min-height: 100vh */
  }
  .version-footer {
    margin-top: 12px;
    padding-top: 10px;
    padding: 6px;
  }
  
  .version-info {
    gap: 6px;
    flex-direction: column;
    text-align: center;
  }
  
  .version-label {
    font-size: 9px;
    padding: 2px 8px;
  }
  
  .build-time {
    font-size: 8px;
  }
  
  .content-section {
    padding: 4px;
  }
  
  .auth-container {
    padding: 12px;
    width: 100%;
    height: 100%; /* Remove min-height: 100vh */
  }
  
  .auth-content {
    padding: 16px;
    max-width: 95%;
    border-radius: 8px;
  }
  
  .auth-content h1 {
    font-size: 16px;
    margin-bottom: 12px;
    line-height: 1.3;
    text-align: center;
  }
  
  .section-card {
    padding: 8px;
    margin-bottom: 6px;
    border-radius: 6px;
  }
}

/* Medium (Tablet Portrait - 481px to 768px) */
@media (min-width: 481px) and (max-width: 768px) {
  .version-footer {
    margin-top: 16px;
    padding-top: 12px;
    padding: 10px;
  }
  
  .version-info {
    gap: 8px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .version-label {
    font-size: 10px;
    padding: 3px 10px;
  }
  
  .build-time {
    font-size: 9px;
  }
  
  .content-section {
    padding: 8px;
  }
  
  .auth-container {
    padding: 16px;
  }
  
  .auth-content {
    padding: 20px;
    max-width: 90%;
    border-radius: 10px;
  }
  
  .auth-content h1 {
    font-size: 20px;
    margin-bottom: 16px;
  }
  
  .section-card {
    padding: 12px;
    margin-bottom: 8px;
    border-radius: 8px;
  }
}

/* Large (Tablet Landscape & Small Desktop - 769px to 1024px) */
@media (min-width: 769px) and (max-width: 1024px) {
  .version-footer {
    margin-top: 20px;
    padding-top: 16px;
    padding: 12px;
  }
  
  .version-info {
    gap: 12px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .version_label {
    font-size: 11px;
    padding: 4px 12px;
  }
  
  .build-time {
    font-size: 10px;
  }
  
  .content-section {
    padding: 12px;
  }
  
  .auth-container {
    padding: 20px;
  }
  
  .auth-content {
    padding: 24px;
    max-width: 85%;
    border-radius: 12px;
  }
  
  .auth-content h1 {
    font-size: 24px;
    margin-bottom: 20px;
  }
  
  .section-card {
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 10px;
  }
}

/* Extra Large (Desktop - 1025px and above) */
@media (min-width: 1025px) {
  .version-footer {
    margin-top: 24px;
    padding-top: 18px;
    padding: 14px;
  }
  
  .version-info {
    gap: 16px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .version-label {
    font-size: 12px;
    padding: 5px 14px;
  }
  
  .build-time {
    font-size: 11px;
  }
  
  .content-section {
    padding: 16px;
  }
  
  .auth-container {
    padding: 24px;
  }
  
  .auth-content {
    padding: 28px;
    max-width: 80%;
    border-radius: 14px;
  }
  
  .auth-content h1 {
    font-size: 28px;
    margin-bottom: 24px;
  }
  
  .section-card {
    padding: 18px;
    margin-bottom: 14px;
    border-radius: 12px;
  }
}
</style>
